// News Block Component
// Display news stories with photos and rich text
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Pencil, Trash2, Plus, Newspaper, Image as ImageIcon } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../services/api';

const NewsBlock = ({ content, padding, backgroundColor, gridWidth, gridHeight, onUpdate, onDelete }) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [stories, setStories] = useState(content?.stories || []);
  const [editPadding, setEditPadding] = useState(padding || 'medium');
  const [editBackgroundColor, setEditBackgroundColor] = useState(backgroundColor || '');
  const [editGridWidth, setEditGridWidth] = useState(gridWidth || 12);
  const [editGridHeight, setEditGridHeight] = useState(gridHeight || 4);
  const [uploading, setUploading] = useState(false);

  const handlePhotoUpload = async (e, storyIndex) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.request('/upload/single', {
        method: 'POST',
        body: formData
      });

      const imageUrl = response.file?.url || response.url;
      const newStories = [...stories];
      newStories[storyIndex].photoUrl = imageUrl;
      setStories(newStories);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleAddStory = () => {
    if (stories.length >= 5) {
      alert('Maximum of 5 stories allowed');
      return;
    }
    setStories([
      ...stories,
      {
        id: Date.now().toString(),
        photoUrl: '',
        header: '',
        body: ''
      }
    ]);
  };

  const handleRemoveStory = (index) => {
    setStories(stories.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    onUpdate({
      content: { stories },
      padding: editPadding,
      backgroundColor: editBackgroundColor || null,
      gridWidth: editGridWidth,
      gridHeight: editGridHeight
    });
    setShowEditModal(false);
  };

  const handleCancel = () => {
    setStories(content?.stories || []);
    setEditPadding(padding || 'medium');
    setEditBackgroundColor(backgroundColor || '');
    setEditGridWidth(gridWidth || 12);
    setEditGridHeight(gridHeight || 4);
    setShowEditModal(false);
  };

  const openEditModal = () => {
    setStories(content?.stories || []);
    setEditPadding(padding || 'medium');
    setEditBackgroundColor(backgroundColor || '');
    setEditGridWidth(gridWidth || 12);
    setEditGridHeight(gridHeight || 4);
    setShowEditModal(true);
  };

  const getPaddingClass = () => {
    switch (padding) {
      case 'none': return 'p-0';
      case 'small': return 'p-2';
      case 'large': return 'p-6';
      default: return 'p-4';
    }
  };

  // Render edit modal
  const renderEditModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-100">
            Edit News Section
          </h3>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {/* Stories */}
          <div>
            <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-100 mb-3">
              News Stories (up to 5)
            </label>

            {stories.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No stories added yet</p>
            ) : (
              <div className="space-y-4">
                {stories.map((story, index) => (
                  <div key={story.id} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium text-sm">Story {index + 1}</span>
                      <button
                        onClick={() => handleRemoveStory(index)}
                        className="text-red-600 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    </div>

                    {/* Photo */}
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Photo
                      </label>
                      <div className="flex items-start gap-3">
                        {story.photoUrl && (
                          <img
                            src={story.photoUrl}
                            alt="Story"
                            className="h-16 w-24 object-cover rounded"
                          />
                        )}
                        <label className="inline-flex items-center px-3 py-1.5 text-xs bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-500">
                          <ImageIcon className="w-3 h-3 mr-1" />
                          {story.photoUrl ? 'Change' : 'Upload'}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handlePhotoUpload(e, index)}
                            className="hidden"
                            disabled={uploading}
                          />
                        </label>
                      </div>
                    </div>

                    {/* Header */}
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Header
                      </label>
                      <input
                        type="text"
                        value={story.header}
                        onChange={(e) => {
                          const newStories = [...stories];
                          newStories[index].header = e.target.value;
                          setStories(newStories);
                        }}
                        placeholder="Story headline"
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-600"
                      />
                    </div>

                    {/* Body */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Body
                      </label>
                      <ReactQuill
                        theme="snow"
                        value={story.body}
                        onChange={(content) => {
                          const newStories = [...stories];
                          newStories[index].body = content;
                          setStories(newStories);
                        }}
                        modules={{
                          toolbar: [
                            ['bold', 'italic'],
                            ['link'],
                            ['clean']
                          ]
                        }}
                        className="bg-white dark:bg-gray-600 text-sm"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {stories.length < 5 && (
              <button
                onClick={handleAddStory}
                className="mt-4 w-full px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
              >
                <Plus className="w-4 h-4 inline mr-1" />
                Add Story
              </button>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-gray-800 p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-[#3C3C3C] dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-[#7A99D8] text-white rounded-lg hover:bg-[#6A89C8]"
          >
            Update News
          </button>
        </div>
      </div>
    </div>
  );

  // Empty state
  if (!content?.stories || content.stories.length === 0) {
    return (
      <>
        {/* Hidden edit trigger for PageBuilder */}
        <button
          data-edit-trigger
          onClick={openEditModal}
          className="hidden"
          aria-hidden="true"
        />
        <div
          className={`${getPaddingClass()} h-full flex items-center justify-center`}
          style={{ backgroundColor }}
        >
          <div className="text-center">
            <Newspaper className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Use edit button to add news stories</p>
          </div>
        </div>
        {showEditModal && createPortal(renderEditModal(), document.body)}
      </>
    );
  }

  // Display news
  return (
    <>
      {/* Hidden edit trigger for PageBuilder */}
      <button
        data-edit-trigger
        onClick={openEditModal}
        className="hidden"
        aria-hidden="true"
      />
      <div
        className={`${getPaddingClass()} h-full relative`}
        style={{ backgroundColor }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 h-full overflow-y-auto">
          {content.stories.map((story) => (
            <div key={story.id} className="bg-white dark:bg-gray-700 rounded-lg shadow-sm overflow-hidden">
              {story.photoUrl && (
                <img
                  src={story.photoUrl}
                  alt={story.header}
                  className="w-full h-32 object-cover"
                />
              )}
              <div className="p-4">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  {story.header}
                </h4>
                <div
                  className="text-sm text-gray-600 dark:text-gray-300 prose prose-sm"
                  dangerouslySetInnerHTML={{ __html: story.body }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Edit/Delete buttons */}
        <div className="absolute top-2 right-2 flex gap-1">
          <button
            onClick={openEditModal}
            className="p-2 bg-white/90 dark:bg-gray-800/90 border border-gray-300 dark:border-gray-600 rounded shadow-sm hover:bg-white dark:hover:bg-gray-700"
            title="Edit news"
          >
            <Pencil className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </button>
          {onDelete && (
            <button
              onClick={onDelete}
              className="p-2 bg-white/90 dark:bg-gray-800/90 border border-gray-300 dark:border-gray-600 rounded shadow-sm hover:bg-red-50 dark:hover:bg-red-900/20"
              title="Delete block"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          )}
        </div>
      </div>

      {showEditModal && createPortal(renderEditModal(), document.body)}
    </>
  );
};

export default NewsBlock;
